/**
 * ADDAN TÜRETİLEN ETKİNLİK KAPAĞI — TEK KAYNAK (web + mobil).
 *
 * Referans: `docs/design/reference/anasayfa-uc-yon.dc.html` bölüm 00 "Kapak
 * görseli olmayan etkinlik". Kural (tasarımın kendi cümlesi): *etkinlik adının
 * karmasından palet ve kırık hat açısı seçilir. Aynı etkinlik her yerde aynı
 * kapağı alır. Zemin birincil veya vurgu rampasından, hat markanın kırık W
 * motifinden, üstünde etkinliğin baş harfleri. Gerçek kapak yüklendiğinde bu
 * katman fotoğrafla değişir.*
 *
 * NEDEN `shared`: karma iki platformda AYNI olmak zorundadır; kopyalanan karma
 * ilk düzeltmede ayrışır (kapak yer tutucusunun dersi, `cover-placeholder.ts`).
 * Burada yalnız SAF hesap var: renk, baş harf ve hat geometrisi. Çizim
 * (SVG/`<Image>`) platformun işidir.
 *
 * TÜRKÇE: baş harf `toLocaleUpperCase('tr-TR')` ile alınır (i → İ, ı → I);
 * sözcük başı `\p{L}` ile tanınır — tasarım prototipindeki `[a-zçğıöşü]`
 * sınıfı büyük İ'yi kaçırıyordu ("İstanbul Ürün…" → "ÜK" oluyordu).
 */
import { lightColors, palette } from './tokens';
/**
 * Dört palet — referans `COVER` dizisiyle birebir; renkler bileşene
 * SABİTLENMEZ, rampadan okunur (marka katmanı değiştirilebilir kalır).
 * Dördüncü varyant referans notu gereği yeşil DEĞİL terrakota: `#17795B`
 * yalnız durum rengidir.
 */
export const EVENT_COVER_PALETTES = [
    {
        background: palette.primary[700],
        stroke: palette.primary[500],
        accent: palette.accent[600],
        foreground: lightColors.textOnPrimary,
    },
    {
        background: palette.accent[700],
        stroke: palette.accent[600],
        accent: palette.accent[500],
        foreground: lightColors.textOnPrimary,
    },
    {
        background: palette.primary[600],
        stroke: palette.primary[500],
        accent: palette.accent[500],
        foreground: lightColors.textOnPrimary,
    },
    {
        background: palette.accent[700],
        stroke: palette.accent[600],
        accent: palette.accent[200],
        foreground: lightColors.textOnPrimary,
    },
];
/** Hat kalınlıkları — referansın çizdiği üç ölçek (kart 340×150 · 56 px · 44 px kare). */
export const EVENT_COVER_STROKE = {
    card: 46,
    tile: 20,
    tileSmall: 16,
};
/** Karma girdisi: kenar boşluğu ve çoklu boşluk fark yaratmaz. */
function normalizeName(name) {
    return name.trim().replace(/\s+/g, ' ');
}
/**
 * Referans prototipiyle (ve `monogramToneIndex` ile) AYNI karma: 31 çarpanı,
 * 9973 modülü. Aynı ad → aynı sayı; Türkçe karakterler BMP'dedir, `charCodeAt`
 * deterministiktir.
 */
export function eventCoverHash(name) {
    const text = normalizeName(name);
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) % 9973;
    }
    return hash;
}
export function eventCoverPaletteIndex(name) {
    return eventCoverHash(name) % EVENT_COVER_PALETTES.length;
}
export function eventCoverPalette(name) {
    return EVENT_COVER_PALETTES[eventCoverPaletteIndex(name)] ?? EVENT_COVER_PALETTES[0];
}
/**
 * Baş harfler: harfle başlayan ilk iki sözcüğün ilk harfi ("2026" atlanır).
 * Hiç harfli sözcük yoksa adın ilk iki karakteri (boş kutu çizilmez).
 */
export function eventCoverInitials(name) {
    const text = normalizeName(name);
    if (text.length === 0)
        return '?';
    const words = text.split(' ').filter((word) => /^\p{L}/u.test(word));
    const source = words.length > 0
        ? words.slice(0, 2).map((word) => Array.from(word)[0] ?? '')
        : Array.from(text).slice(0, 2);
    return source.join('').toLocaleUpperCase('tr-TR');
}
const round = (value) => Math.round(value * 100) / 100;
/**
 * Kırık hat açısı da addan türer (referans `paths()`): dikey konum karmanın
 * 5'e ve 7'ye bölümünden, yön tek/çift olmasından. Koordinatlar verilen
 * kutuya göredir; çağıran aynı `width × height` ile `viewBox` kurar.
 */
export function eventCoverPaths(name, width, height) {
    const k = eventCoverHash(name);
    const y1 = height * (0.55 + (k % 5) * 0.06);
    const y2 = height * (0.18 + (k % 7) * 0.05);
    const dir = k % 2 ? 1 : -1;
    return {
        path1: `M${round(-width * 0.08)} ${round(y1)} L${round(width * 0.42)} ${round(y1 + height * 0.42 * dir)}`,
        path2: `M${round(width * 0.6)} ${round(y2 + height * 0.55 * dir)} L${round(width * 1.08)} ${round(y2 - height * 0.3 * dir)}`,
    };
}
/** Tek çağrıda kapak: palet + baş harf + hat geometrisi. */
export function deriveEventCover(name, box) {
    return {
        initials: eventCoverInitials(name),
        palette: eventCoverPalette(name),
        ...eventCoverPaths(name, box.width, box.height),
    };
}
//# sourceMappingURL=event-cover.js.map